<template>
    <v-btn
        v-if="dialogButton"
        :color="buttonColor"
        :icon="true"
        dark
        variant="plain"
        @click="open"
    >
        <v-icon>{{ buttonIcon }}</v-icon>
    </v-btn>
    <v-dialog
        v-model="dialog"
        :activator="activatorRef ?? undefined"
        :max-width="maxWidth"
        :persistent="persistent"
    >
        <v-card>
            <v-card-title>
                <span class="text-h5">{{ title }}</span>
            </v-card-title>
            <slot name=subTitle>
            </slot>
            <v-card-text>
                <v-progress-linear v-if="(progressLinerOnConfirm && confirming)" :color="progressLineColor"
                                   :indeterminate="true"/>
                <template v-else>
                    <slot name="formItems" v-bind:onCancel="close" v-bind:onSave="confirmHandler">
                    </slot>
                </template>
            </v-card-text>
            <action-btn
                v-if="!hideActionBtns"
                :cancel="cancel"
                :cancel-color="cancelColor"
                :cancel-disabled="cancelDisabled"
                :cancel-text="cancelText"
                :cancel-variant="cancelVariant"
                :confirm="confirm"
                :confirm-color="confirmColor"
                :confirm-disabled="confirmDisabled"
                :confirm-text="confirmText"
                :confirm-variant="confirmVariant"
                @cancel="close"
                @save="confirmHandler"
            />
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import type {PropType, WritableComputedRef} from "vue";
import {defineComponent, ref} from "vue";
import {useVModel} from "@vueuse/core";
import type {TEmptyAsyncFunc} from "@/components/generics/types/TEmptyAsyncFunc";
import type {TConfirmType} from "./types/TConfirmType";
import {ActionBtnProps} from "@/components/generics/ActionBtn/ActionBtnProps";
import ActionBtn from "@/components/generics/ActionBtn/ActionBtn.vue";
import {useI18n} from "vue-i18n";
import {useKMessageStore} from "@/components/dds/KMessageStore/KMessageStore";

export default defineComponent({
    components: {ActionBtn},
    emits: ['update:modelValue', 'cancel', 'confirm', 'confirming'],
    props: {
        ...ActionBtnProps,
        modelValue: {
            type: Boolean,
            default: false
        },
        maxWidth: {
            type: String,
            default: '50vw'
        },
        title: {
            type: String,
            default: ''
        },
        closeOnConfirm: {
            type: Boolean,
            default: true
        },
        closeOnCancel: {
            type: Boolean,
            default: true
        },
        buttonIcon: {
            type: String,
            default: 'mdi-info'
        },
        dialogButton: {
            type: Boolean,
            default: false
        },
        buttonColor: {
            type: String,
            default: 'primary'
        },
        persistent: {
            type: Boolean,
            default: false
        },
        progressLinerOnConfirm: {
            type: Boolean,
            default: false
        },
        progressLineColor: {
            type: String,
            default: "primary"
        },
        activator: {
            type: String,
        },
        // can take a function instead of listening to the confirm event, this gives the ability to
        // keep the modal open and show the progress liner
        onConfirming: {
            type: Function as PropType<TEmptyAsyncFunc>,
        },
        // the type determines if the dialog should give a save or delete message if custom is used
        // u need to take care about informing
        confirmType: {
            type: String as PropType<TConfirmType>,
            default: 'custom'
        },
        hideActionBtns: {
            type: Boolean,
            default: false
        },
        suppressConfirmMessage:{
            type: Boolean,
            default: false
        },
        suppressCancelMessage:{
            type: Boolean,
            default: false
        }

    },
    setup(props, {emit}) {
        const {t} = useI18n();
        const messageStore = useKMessageStore();
        const dialog = <WritableComputedRef<boolean>>useVModel(props, 'modelValue', emit, {passive: true})
        const confirming = ref<boolean>(false);
        const activatorRef = ref<string>("")
        activatorRef.value = props.activator ?? "";

        const close = () => {
            if (props.closeOnCancel) {
                dialog.value = false;
            }
            emit("cancel");
        }

        const confirmHandler = async () => {
            emit("confirm");
            emit("confirming", true);
            try {
                confirming.value = true;
                if (props.onConfirming) {
                    await props.onConfirming();
                }
                if (props.confirmType === 'save' && !props.suppressConfirmMessage) {
                    messageStore.push('success', t('generic.dialog.save',0))
                } else if (props.confirmType === 'delete' && !props.suppressCancelMessage) {
                    messageStore.push('success', t('generic.dialog.delete',0))
                }

            } catch (e) {
                if (props.confirmType === 'save' && !props.suppressConfirmMessage) {
                    messageStore.push('error', t('generic.dialog.save',1))
                }
                if (props.confirmType === 'delete' && !props.suppressCancelMessage) {
                    messageStore.push('error', t('generic.dialog.delete',1))
                }
                console.error(e)
            }
            confirming.value = false;
            emit("confirming", false);
            if (props.closeOnConfirm) {
                dialog.value = false
            }
        }

        const open = () => {
            dialog.value = true
        }

        return {
            //ref
            activatorRef,
            dialog,
            confirmHandler,
            confirming,
            //action
            close,
            open,
        }
    },
})
</script>
